import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, FormControlLabel, Checkbox,
  MenuItem, Grid, Typography, Box, Divider, Chip,
  useTheme, styled
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.shape.borderRadius * 2,
    background: theme.palette.background.paper,
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(2, 3),
  '& .MuiTypography-root': {
    fontWeight: 600,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius,
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 3),
  textTransform: 'none',
  fontWeight: 600,
}));

const RedirectionModal = ({ open, onClose, onSubmit, initialData }) => {
  const theme = useTheme();
  const [form, setForm] = useState({
    source: '',
    destination: '',
    match_type: '',
    ignore_case: false,
    redirect_type: '',
    status: false,
    category: '',
    activation_date: '',
    deactivation_date: '',
  });

  useEffect(() => {
    if (initialData) {
      setForm({
        ...initialData,
        activation_date: initialData.activation_date || '',
        deactivation_date: initialData.deactivation_date || '',
        ignore_case: initialData.ignore_case || false,
      });
    } else {
      setForm({
        source: '',
        destination: '',
        match_type: '',
        ignore_case: false,
        redirect_type: '',
        status: false,
        category: '',
        activation_date: '',
        deactivation_date: '',
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = () => {
    onSubmit(form);
  };

  return (
    <StyledDialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <StyledDialogTitle>
        {initialData ? 'Update Redirection Rule' : 'Create New Redirection Rule'}
      </StyledDialogTitle>
      
      <DialogContent dividers sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <InfoOutlined color="primary" sx={{ mr: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Fill in the fields below to {initialData ? 'update' : 'create'} a redirection entry. Fields marked with * are required.
          </Typography>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <StyledTextField
              fullWidth
              required
              label="Source URL"
              name="source"
              placeholder="/old-page"
              value={form.source}
              onChange={handleChange}
              margin="normal"
              variant="outlined"
              helperText="The original URL that will be redirected"
              InputProps={{
                startAdornment: (
                  <Typography color="text.secondary" sx={{ mr: 1 }}>/</Typography>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <StyledTextField
              fullWidth
              required
              label="Target URL"
              name="destination"
              placeholder="/new-page"
              value={form.destination}
              onChange={handleChange}
              margin="normal"
              variant="outlined"
              helperText="The target URL where users will be redirected"
              InputProps={{
                startAdornment: (
                  <Typography color="text.secondary" sx={{ mr: 1 }}>/</Typography>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <StyledTextField
              select
              fullWidth
              required
              label="Match Type"
              name="match_type"
              value={form.match_type}
              onChange={handleChange}
              margin="normal"
              variant="outlined"
              helperText="How the source URL should be matched"
            >
              <MenuItem value="">Select match type</MenuItem>
              <MenuItem value="exact">
                <Box>
                  <Typography>Exact Match</Typography>
                  <Typography variant="caption" color="text.secondary">
                    URL must match exactly
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="startsWith">
                <Box>
                  <Typography>Starts With</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Match all URLs starting with this path
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="regex">
                <Box>
                  <Typography>Regular Expression</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Match using regex pattern
                  </Typography>
                </Box>
              </MenuItem>
            </StyledTextField>

            {form.match_type === 'regex' && (
              <Box mt={1} p={1.5} bgcolor={theme.palette.grey[100]} borderRadius={1}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Regex Example:</strong> <code>^/product/.*</code> — This will match any URL starting with <code>/product/</code>
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box mt={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="ignore_case"
                    checked={form.ignore_case}
                    onChange={handleChange}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography>Ignore Case</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Make matching case-insensitive
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <StyledTextField
              select
              fullWidth
              required
              label="Redirect Type"
              name="redirect_type"
              value={form.redirect_type}
              onChange={handleChange}
              margin="normal"
              variant="outlined"
              helperText="The HTTP status code used for the redirection"
            >
              <MenuItem value="">Select redirect type</MenuItem>
              <MenuItem value="301">
                <Box>
                  <Typography>301 - Permanent Redirect</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Recommended for SEO
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="302">
                <Box>
                  <Typography>302 - Temporary Redirect</Typography>
                  <Typography variant="caption" color="text.secondary">
                    For temporary URL changes
                  </Typography>
                </Box>
              </MenuItem>
            </StyledTextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <StyledTextField
              fullWidth
              label="Category"
              name="category"
              value={form.category}
              onChange={handleChange}
              margin="normal"
              variant="outlined"
              placeholder="e.g., campaign, SEO"
              helperText="Used for grouping or identifying redirect purpose"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box mt={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="status"
                    checked={form.status}
                    onChange={handleChange}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography>Active Redirect</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Enable this redirect rule
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <StyledTextField
              fullWidth
              type="date"
              label="Activation Date"
              name="activation_date"
              value={form.activation_date?.split('T')[0] || ''}
              onChange={handleChange}
              margin="normal"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              helperText="Date when this redirect should start working"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <StyledTextField
              fullWidth
              type="date"
              label="Deactivation Date"
              name="deactivation_date"
              value={form.deactivation_date?.split('T')[0] || ''}
              onChange={handleChange}
              margin="normal"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              helperText="Date when this redirect should stop working"
            />
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ p: 3 }}>
        <StyledButton onClick={onClose} variant="outlined">
          Cancel
        </StyledButton>
        <StyledButton 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={!form.source || !form.destination || !form.match_type || !form.redirect_type}
        >
          {initialData ? 'Update Rule' : 'Create Rule'}
        </StyledButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default RedirectionModal;